import type { Unsubscribe } from '@hgc/core';

/**
 * 一个"当前值 + 订阅变化"的最小可观察值（`useSyncExternalStore` 可直接消费）。
 *
 * 为什么不用 `@hgc/core` 的 `createObservableValue`：那个 API 只提供订阅，**不提供订阅者数量**。
 * 而界面这一层最容易犯的错是订阅忘记取消 —— 它的表现是"越开越卡"，不是任何一处报错。
 * 这里多出来的 `listenerCount` 就是为了让测试能直接断言"卸载后归零"。
 *
 * 值相等（`Object.is`）时不通知：遥测每秒几十帧，每次通知都是一次 React 重渲染。
 */
export class Signal<T> {
  private current: T;
  private readonly listeners = new Set<() => void>();

  constructor(initial: T) {
    this.current = initial;
  }

  get(): T {
    return this.current;
  }

  set(next: T): void {
    if (Object.is(this.current, next)) return;
    this.current = next;
    // 先快照再派发：订阅者在回调里退订是常见写法（与 core 的事件总线同理）
    for (const listener of [...this.listeners]) listener();
  }

  readonly subscribe = (listener: () => void): Unsubscribe => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  get listenerCount(): number {
    return this.listeners.size;
  }

  /** 销毁时调用：把订阅者清掉，避免"已经没人持有这个对象了，它还挂在别人的回调里" */
  clear(): void {
    this.listeners.clear();
  }
}
